import fetch from '../utils/fetch';
import CustomError from '../utils/CustomError';
import { installedProductsV4 } from '../constants/endpoints';
import { UNEXPECTED_ERROR } from '../constants/services';

/**
 * Retrieves the details of a specific location by its ID
 *
 * @async
 * @function getTurbineById
 * @param {string} token - Bearer authentication token for the API
 * @param {string} turbineId - Unique identifier of the location to retrieve
 * @returns {Promise<Object>} Object containing the location data
 */
export async function getTurbinesByLocationId(token, locationId) {
  try {
    const query = await fetch(
      `${installedProductsV4}/locations/${locationId}`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        }
      }
    );
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Retrieves the details of a specific turbine by its ID
 *
 * @async
 * @function getTurbineDetailsServ
 * @param {string} token - Bearer authentication token for the API
 * @param {string} turbineId - Unique identifier of the turbine to retrieve
 * @returns {Promise<Object>} Object containing the turbine detail data
 */
export async function getTurbineDetailsServ(token, turbineId) {
  try {
    const query = await fetch(`${installedProductsV4}/${turbineId}`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Retrieves a list of all turbines
 *
 * @async
 * @function getTurbinesListServ
 * @param {string} token - Bearer authentication token for the API
 * @returns {Promise<Object>} Object containing the turbines list data
 */
export async function getTurbinesListServ(token, params = {}) {
  try {
    const query = await fetch(installedProductsV4, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: {
        type: 'TURBINE',
        ...params
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Retrieves a list of active turbines
 *
 * @async
 * @function getActiveTurbinesListServ
 * @param {string} token - Bearer authentication token for the API
 * @returns {Promise<Object>} Object containing the active turbines list data
 */
export async function getActiveTurbinesListServ(token) {
  try {
    const query = await fetch(installedProductsV4, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: {
        type: 'TURBINE',
        status: 'active'
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Creates a new turbine
 *
 * @async
 * @function createTurbineServ
 * @param {string} token - Bearer authentication token for the API
 * @param {object} turbineData - Turbine data to create
 * @returns {Promise<Object>} Object containing the created turbine data
 */
export async function createTurbineServ(token, turbineData) {
  try {
    const query = await fetch(installedProductsV4, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: {
        type: 'TURBINE'
      },
      body: JSON.stringify(turbineData)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Updates an existing turbine
 *
 * @async
 * @function updateTurbineServ
 * @param {string} token - Bearer authentication token for the API
 * @param {string} turbineId - ID of the turbine to update
 * @param {object} turbineData - Turbine data to update
 * @returns {Promise<Object>} Object containing the updated turbine data
 */
export async function updateTurbineServ(token, turbineId, turbineData) {
  try {
    const query = await fetch(`${installedProductsV4}/${turbineId}`, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: {
        type: 'TURBINE'
      },
      body: JSON.stringify(turbineData)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * Deletes an existing turbine
 *
 * @async
 * @function deleteTurbineServ
 * @param {string} token - Bearer authentication token for the API
 * @param {string} turbineId - ID of the turbine to delete
 * @returns {Promise<Object>} Object containing the response data
 */
export async function deleteTurbineServ(token, turbineId) {
  try {
    const query = await fetch(`${installedProductsV4}/${turbineId}`, {
      method: 'delete',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: {
        type: 'TURBINE'
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export default {
  getTurbinesByLocationId,
  getTurbineDetailsServ,
  getTurbinesListServ,
  getActiveTurbinesListServ,
  createTurbineServ,
  updateTurbineServ,
  deleteTurbineServ
};
